/**
 * VH-83: Recipe library page.
 *
 * Lists the user's recipes as a responsive card grid. Filters: category,
 * search, "compatible only" toggle. Empty/loading/error states. The
 * "+ New Recipe" CTA navigates to /recipes/new (capture flow).
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlusIcon, BookOpenIcon } from '@heroicons/react/24/outline'
import { useRecipes, useRecipeCategories } from '../hooks/useRecipes'
import RecipeCard from '../components/recipes/RecipeCard'
import RecipeCategoryFilter from '../components/recipes/RecipeCategoryFilter'
import { contentLanguage } from '../i18n/supported'

export default function Recipes() {
  const { t, i18n } = useTranslation()
  const lang = contentLanguage(i18n?.language)

  const [categoryId, setCategoryId] = useState(null)
  const [compatibleOnly, setCompatibleOnly] = useState(false)
  const [search, setSearch] = useState('')

  const params = {}
  if (categoryId) params.category_id = categoryId
  if (compatibleOnly) params.compatible_only = true
  if (search.trim()) params.q = search.trim()

  const { data, isLoading, error, refetch } = useRecipes(params)
  const { data: categories = [] } = useRecipeCategories(lang)

  const items = data?.items || []
  const categoryById = Object.fromEntries(categories.map((c) => [c.id, c]))

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="recipes-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpenIcon className="h-8 w-8 text-primary-600" />
            {t('recipes.title')}
          </h1>
          <p className="mt-1 text-gray-600">{t('recipes.subtitle')}</p>
        </div>
        <Link
          to="/recipes/new"
          className="btn-primary flex items-center gap-2"
          data-testid="new-recipe-link"
        >
          <PlusIcon className="h-4 w-4" />
          {t('recipes.newRecipe')}
        </Link>
      </div>

      <div className="mb-6">
        <RecipeCategoryFilter
          categories={categories}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          compatibleOnly={compatibleOnly}
          onCompatibleOnlyChange={setCompatibleOnly}
          search={search}
          onSearchChange={setSearch}
        />
      </div>

      {isLoading && (
        <div className="text-center text-gray-500 py-12">
          {t('recipes.loading')}
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <p className="text-red-600">{t('recipes.errorLoading')}</p>
          <button onClick={refetch} className="btn-secondary mt-2">
            {t('recipes.retry')}
          </button>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <BookOpenIcon className="h-12 w-12 mx-auto text-gray-300" />
          <p className="mt-2 text-gray-600">
            {categoryId || compatibleOnly || search.trim()
              ? t('recipes.noRecipesFiltered')
              : t('recipes.noRecipes')}
          </p>
          <Link to="/recipes/new" className="btn-primary mt-4 inline-flex">
            {t('recipes.newRecipe')}
          </Link>
        </div>
      )}

      {items.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          data-testid="recipe-grid"
        >
          {items.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              categoryLabel={
                categoryById[recipe.category_id]?.display_name ||
                recipe.category_key
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
